import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { ArrowLeft, Download, FileText } from 'lucide-react';
import API_URL from '../config';

const API_BASE = API_URL;

export default function HistoriqueContractuel() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { isManager } = useAuth();

  const [versions, setVersions] = useState([]);
  const [userName, setUserName] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isManager) { navigate('/gestionShifts'); return; }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, isManager]);

  const fetchData = async () => {
    setLoading(true);
    const token = localStorage.getItem('token');
    try {
      const [userRes, versionsRes] = await Promise.all([
        fetch(`${API_BASE}/users/${userId}`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_BASE}/users/${userId}/contract-versions`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      if (userRes.ok) {
        const u = await userRes.json();
        setUserName(u.full_name || u.email);
      }
      if (versionsRes.ok) {
        setVersions(await versionsRes.json());
      }
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = () => {
    const headers = ['Date effet', 'Salaire brut (€)', 'Charges pat. (€)', 'Heures/sem', 'Jours/sem', 'Type contrat', 'Début contrat', 'Fin contrat', 'Fin essai', 'Note'];
    const rows = versions.map(v => [
      v.effective_from || 'Initial',
      v.monthly_gross_salary || '',
      v.employer_charges || '',
      v.weekly_hours || '',
      v.work_days ? v.work_days.map(d => DAY_LABELS[d]).join(' ') : '',
      v.contract_type || '',
      v.contract_start_date || '',
      v.contract_end_date || '',
      v.trial_period_end_date || '',
      `"${(v.note || '').replace(/"/g, '""')}"`,
    ]);
    const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `historique_contrat_${userId}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const fmt = (val) => val ? Number(val).toLocaleString('fr-FR', { minimumFractionDigits: 2 }) + ' €' : '—';
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR') : '—';
  const DAY_LABELS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
  const fmtWorkDays = (days) => {
    if (!days || days.length === 0) return '—';
    return days.map(d => DAY_LABELS[d]).join(', ');
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/utilisateurs')}
              className="p-2 text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                <FileText className="w-5 h-5 text-indigo-600" />
                Historique contractuel
              </h1>
              {userName && <p className="text-sm text-gray-500 mt-0.5">{userName}</p>}
            </div>
          </div>
          <button
            onClick={handleExportCSV}
            disabled={versions.length === 0}
            className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-indigo-600 bg-indigo-50 border border-indigo-200 rounded-xl hover:bg-indigo-100 disabled:opacity-50 transition-colors"
          >
            <Download className="w-4 h-4" /> Exporter CSV
          </button>
        </div>

        {/* Table */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          {loading ? (
            <div className="p-12 text-center text-gray-400 text-sm">Chargement…</div>
          ) : versions.length === 0 ? (
            <div className="p-12 text-center text-gray-400 text-sm">Aucun historique contractuel.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  <tr>
                    <th className="px-5 py-4 text-left">Date d'effet</th>
                    <th className="px-5 py-4 text-left">Salaire brut</th>
                    <th className="px-5 py-4 text-left">Charges pat.</th>
                    <th className="px-5 py-4 text-left">Heures/sem</th>
                    <th className="px-5 py-4 text-left">Jours trav.</th>
                    <th className="px-5 py-4 text-left">Type</th>
                    <th className="px-5 py-4 text-left">Début contrat</th>
                    <th className="px-5 py-4 text-left">Fin essai</th>
                    <th className="px-5 py-4 text-left">Note</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {versions.map((v, idx) => (
                    <tr key={v.id} className={`hover:bg-indigo-50/30 transition-colors ${idx === 0 ? 'font-medium' : ''}`}>
                      <td className="px-5 py-4 text-gray-800">
                        {v.effective_from
                          ? fmtDate(v.effective_from)
                          : <span className="text-gray-400 italic text-xs">Initial</span>}
                        {idx === 0 && (
                          <span className="ml-2 text-[10px] bg-indigo-100 text-indigo-700 font-semibold px-2 py-0.5 rounded-full">En cours</span>
                        )}
                      </td>
                      <td className="px-5 py-4">{fmt(v.monthly_gross_salary)}</td>
                      <td className="px-5 py-4">{fmt(v.employer_charges)}</td>
                      <td className="px-5 py-4">{v.weekly_hours ? `${v.weekly_hours}h` : '—'}</td>
                      <td className="px-5 py-4 text-xs">{fmtWorkDays(v.work_days)}</td>
                      <td className="px-5 py-4">{v.contract_type || '—'}</td>
                      <td className="px-5 py-4">{fmtDate(v.contract_start_date)}</td>
                      <td className="px-5 py-4">{fmtDate(v.trial_period_end_date)}</td>
                      <td className="px-5 py-4 text-gray-500 italic">{v.note || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
